import { FC } from 'react';
import * as stylex from '@stylexjs/stylex';
import { Button, PreloaderCircular } from '@shared/ui/kit';
import { SortIcon } from '@shared/assets/icons';
import { styles } from './styles';
import { MessagesStats } from '@pages/event/api/useFetchMessagesStats';

interface IModalProps {
  closeHandle?: () => void;
  messagesStats: MessagesStats | null;
  showErrorUsers: boolean;
  setShowErrorUsers: React.Dispatch<React.SetStateAction<boolean>>;
  errorChannel: 'sms' | 'email' | 'vk' | null;
  setErrorChannel: React.Dispatch<React.SetStateAction<'sms' | 'email' | 'vk' | null>>;
  isLoading: boolean;
}

export const ModalStatus: FC<IModalProps> = ({
  closeHandle,
  messagesStats,
  showErrorUsers,
  setShowErrorUsers,
  errorChannel,
  setErrorChannel,
  isLoading,
}) => {
  if (!messagesStats) return null;

  const totalUsers = messagesStats.totalMessages;
  const recognizedUsersSms = messagesStats.smsStats.successCount;
  const recognizedUsersEmail = messagesStats.emailStats.successCount;
  const recognizedUsersVk = messagesStats.vkStats.successCount;

  const getWidth = (value: number): string => {
    if (totalUsers === 0) return '0%';
    const percentage = (value / totalUsers) * 100;
    return `${percentage}%`;
  };

  const getPercent = (value: number): number => {
    if (totalUsers === 0) return 0;
    return Math.round((value / totalUsers) * 100);
  };

  const handleBackClick = () => {
    setShowErrorUsers(false);
    setErrorChannel(null);
  };

  if (isLoading) {
    return (
      <div {...stylex.props(styles.loader)}>
        <PreloaderCircular />
      </div>
    );
  }

  const shouldShowSmsBlock = recognizedUsersSms > 0 || messagesStats.smsStats.errorCount > 0;
  const shouldShowEmailBlock = recognizedUsersEmail > 0 || messagesStats.emailStats.errorCount > 0;
  const shouldShowVkBlock = recognizedUsersVk > 0 || messagesStats.vkStats.errorCount > 0;

  return (
    <>
      {!showErrorUsers ? (
        <div>
          <div {...stylex.props(styles.content)}>
            <div {...stylex.props(styles.main)}>
              <div {...stylex.props(styles.infoSection)}>
                <div {...stylex.props(styles.accepted)}>
                  К отправке<p {...stylex.props(styles.progressValue)}>/ {getPercent(totalUsers)}%</p>
                </div>
                {shouldShowSmsBlock && (
                  <div {...stylex.props(styles.notAccepted)}>
                    <div {...stylex.props(styles.statusRow)}>
                      Отправлено СМС<p {...stylex.props(styles.progressValue)}>/ {getPercent(recognizedUsersSms)}%</p>
                    </div>
                    {messagesStats.smsStats.errorCount > 0 && (
                      <span
                        {...stylex.props(styles.openSms)}
                        onClick={() => {
                          setShowErrorUsers(true);
                          setErrorChannel('sms');
                        }}
                      >
                        Показать не доставленные
                      </span>
                    )}
                  </div>
                )}
                {shouldShowEmailBlock && (
                  <div {...stylex.props(styles.unrecongnized)}>
                    <div {...stylex.props(styles.statusRow)}>
                      Отправлено E-mail
                      <p {...stylex.props(styles.progressValue)}>/ {getPercent(recognizedUsersEmail)}%</p>
                    </div>
                    {messagesStats.emailStats.errorCount > 0 && (
                      <span
                        {...stylex.props(styles.openEmail)}
                        onClick={() => {
                          setShowErrorUsers(true);
                          setErrorChannel('email');
                        }}
                      >
                        Показать не доставленные
                      </span>
                    )}
                  </div>
                )}
                {shouldShowVkBlock && (
                  <div {...stylex.props(styles.unrecongnized)}>
                    <div {...stylex.props(styles.statusRow)}>
                      Отправлено VK Teams
                      <p {...stylex.props(styles.progressValue)}>/ {getPercent(recognizedUsersVk)}%</p>
                    </div>
                    {messagesStats.vkStats.errorCount > 0 && (
                      <span
                        {...stylex.props(styles.openEmail)}
                        onClick={() => {
                          setShowErrorUsers(true);
                          setErrorChannel('vk');
                        }}
                      >
                        Показать не доставленные
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div {...stylex.props(styles.infoWidget)}>
                <div {...stylex.props(styles.progressBarContainer)}>
                  <div {...stylex.props(styles.progressBar, styles.total)}>{totalUsers}</div>
                </div>
                {shouldShowSmsBlock && (
                  <div {...stylex.props(styles.progressBarContainer)}>
                    <div {...stylex.props(styles.progressBar, styles.recognized(getWidth(recognizedUsersSms)))}>
                      {recognizedUsersSms}
                    </div>
                  </div>
                )}
                {shouldShowEmailBlock && (
                  <div {...stylex.props(styles.progressBarContainer)}>
                    <div {...stylex.props(styles.progressBar, styles.unrecognized(getWidth(recognizedUsersEmail)))}>
                      {recognizedUsersEmail}
                    </div>
                  </div>
                )}
                {shouldShowVkBlock && (
                  <div {...stylex.props(styles.progressBarContainer)}>
                    <div {...stylex.props(styles.progressBar, styles.unrecognized(getWidth(recognizedUsersVk)))}>
                      {recognizedUsersVk}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div {...stylex.props(styles.userTable)}>
          {(errorChannel === 'sms' && messagesStats.smsStats.errorData.length > 0) ||
          (errorChannel === 'email' && messagesStats.emailStats.errorData.length > 0) ||
          (errorChannel === 'vk' && messagesStats.vkStats.errorData.length > 0) ? (
            <div {...stylex.props(styles.mainError)}>
              <div {...stylex.props(styles.contactType)}>
                <div {...stylex.props(styles.scrollContainer)}>
                  <div {...stylex.props(styles.contactHeader, styles.userTableText)}>
                    <div {...stylex.props(styles.contactInfo)}>
                      <p {...stylex.props(styles.contactItem)}>
                        ФИО <SortIcon />
                      </p>
                    </div>
                    <div {...stylex.props(styles.contactInfo)}>
                      <p {...stylex.props(styles.contactItem)}>
                        {errorChannel === 'sms' ? 'Телефон' : 'E-mail'} <SortIcon />
                      </p>
                    </div>

                    <div {...stylex.props(styles.contactInfo)}>
                      <p {...stylex.props(styles.contactItem)}>
                        Причина <SortIcon />
                      </p>
                    </div>
                  </div>

                  <div {...stylex.props(styles.userTable)}>
                    {errorChannel === 'email' &&
                      messagesStats.emailStats.errorData.map((user, index) => (
                        <div key={`email-${index}`} {...stylex.props(styles.contactUser)}>
                          <div {...stylex.props(styles.fullName)}>
                            <div {...stylex.props(styles.fullNameItem)}>
                              {user.lastName} {user.firstName}
                            </div>
                          </div>
                          <div {...stylex.props(styles.phoneUser)}>
                            <div {...stylex.props(styles.phoneUserItem)}>{user.email || '-'}</div>
                          </div>
                          <div {...stylex.props(styles.errorInfo)}>
                            <div {...stylex.props(styles.errorInfoItem)}>{user.errorText}</div>
                          </div>
                        </div>
                      ))}

                    {errorChannel === 'sms' &&
                      messagesStats.smsStats.errorData.map((user, index) => (
                        <div key={`sms-${index}`} {...stylex.props(styles.contactUser)}>
                          <div {...stylex.props(styles.fullName)}>
                            <div {...stylex.props(styles.fullNameItem)}>
                              {user.lastName} {user.firstName}
                            </div>
                          </div>
                          <div {...stylex.props(styles.phoneUser)}>
                            <div {...stylex.props(styles.phoneUserItem)}>{user.phone || '-'}</div>
                          </div>
                          <div {...stylex.props(styles.errorInfo)}>
                            <div {...stylex.props(styles.errorInfoItem)}>{user.errorText}</div>
                          </div>
                        </div>
                      ))}

                    {errorChannel === 'vk' &&
                      messagesStats.vkStats.errorData.map((user, index) => (
                        <div key={`vk-${index}`} {...stylex.props(styles.contactUser)}>
                          <div {...stylex.props(styles.fullName)}>
                            <div {...stylex.props(styles.fullNameItem)}>
                              {user.lastName} {user.firstName}
                            </div>
                          </div>
                          <div {...stylex.props(styles.phoneUser)}>
                            <div {...stylex.props(styles.phoneUserItem)}>{user.email || '-'}</div>
                          </div>
                          <div {...stylex.props(styles.errorInfo)}>
                            <div {...stylex.props(styles.errorInfoItem)}>{user.errorText}</div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p>Нет данных о недоставленных сообщениях</p>
          )}
        </div>
      )}

      <div {...stylex.props(styles.button)}>
        {showErrorUsers && (
          <Button
            size="small"
            settings={{ width: '96px', fontFamily: 'DIN-2014_Regular' }}
            variant="secondary"
            onClick={handleBackClick}
          >
            Назад
          </Button>
        )}
        <Button
          size="small"
          settings={{ padding: '8px 20px', fontSize: '14px', fontFamily: 'DIN-2014_Regular' }}
          variant="accent"
          onClick={() => {
            setShowErrorUsers(false);
            setErrorChannel(null);
            closeHandle?.();
          }}
        >
          Закрыть
        </Button>
      </div>
    </>
  );
};
